import { RESUME_BUILDER_JOURNEY_STEPS } from '@/components/resume-builder/use-resume-builder-journey-ftue'
import {
  FTUE_FLOW_PRIORITY,
  registerAnchor,
  registerSteps,
  unregisterAnchor,
  useFtueAnchor,
  useFtueStore,
} from '@/ftue'
import { useCallback, useEffect, useMemo, useRef } from 'react'

export const WELCOME_BACK_MENU_FLOW = 'welcome-back-menu'
export const WELCOME_BACK_MENU_PHASE = 'welcome-back-menu-visible-items'
export const WELCOME_BACK_MENU_PRIORITY = FTUE_FLOW_PRIORITY.navigation

export const WELCOME_BACK_MENU_STEPS = {
  overview: 'welcome-back-menu-overview',
  openMobile: 'welcome-back-menu-open-mobile',
} as const

export const WELCOME_BACK_MENU_ANCHORS = {
  overview: 'welcome-back-menu-overview-anchor',
  openMobile: 'welcome-back-menu-open-mobile-anchor',
} as const

export interface WelcomeBackMenuItem {
  /** Stable across labels, ordering, RBAC changes, and responsive layouts. */
  id: string
  body: string
  locked?: boolean
}

export interface WelcomeBackMenuSourceItem {
  hidden?: boolean
  locked?: boolean
  ftue?: Omit<WelcomeBackMenuItem, 'locked'>
  children?: WelcomeBackMenuSourceItem[]
}

/**
 * Flatten exactly what the sidebar renders. A group gets its own orientation
 * only when it contains multiple visible destinations; a one-child group (the
 * restricted Benefits shape) would otherwise produce two duplicate coachmarks.
 */
export function collectVisibleWelcomeBackItems(
  items: WelcomeBackMenuSourceItem[],
): WelcomeBackMenuItem[] {
  const result: WelcomeBackMenuItem[] = []
  const resolved = (item: WelcomeBackMenuSourceItem): WelcomeBackMenuItem | null =>
    item.ftue
      ? { ...item.ftue, ...(item.locked !== undefined ? { locked: item.locked } : {}) }
      : null
  for (const item of items) {
    if (item.hidden) continue
    if (item.children) {
      const children = collectVisibleWelcomeBackItems(item.children)
      const group = resolved(item)
      if (children.length > 1 && group) result.push(group)
      result.push(...children)
      continue
    }
    const leaf = resolved(item)
    if (leaf) result.push(leaf)
  }
  return result
}

export function welcomeBackMenuItemStepId(id: string): string {
  return `welcome-back-menu-item-${id}`
}

function welcomeBackMenuItemAnchorId(id: string): string {
  return `${welcomeBackMenuItemStepId(id)}-anchor`
}

/** Multiple responsive copies may register under the same logical anchor id. */
function useItemAnchorSet() {
  const elements = useRef(new Map<string, HTMLElement>())
  const callbacks = useRef(new Map<string, (element: HTMLElement | null) => void>())

  return useCallback((itemId: string) => {
    const anchorId = welcomeBackMenuItemAnchorId(itemId)
    const existing = callbacks.current.get(anchorId)
    if (existing) return existing

    const callback = (element: HTMLElement | null) => {
      const previous = elements.current.get(anchorId)
      if (previous && previous !== element) unregisterAnchor(anchorId, previous)
      if (element) {
        elements.current.set(anchorId, element)
        registerAnchor(anchorId, element)
      } else {
        elements.current.delete(anchorId)
      }
    }
    callbacks.current.set(anchorId, callback)
    return callback
  }, [])
}

interface WelcomeBackMenuFtueOptions {
  enabled?: boolean
  loginCount: number
  ready: boolean
  items: WelcomeBackMenuItem[]
  isMobile: boolean
  mobileMenuOpen?: boolean
}

/** Show every currently-visible stable navigation destination after login one. */
export function useWelcomeBackMenuFtue({
  enabled = true,
  loginCount,
  ready,
  items,
  isMobile,
  mobileMenuOpen = false,
}: WelcomeBackMenuFtueOptions) {
  const hydrated = useFtueStore((state) => state.hydrated)
  const seen = useFtueStore((state) => state.seen)
  const primaryJourneyActive = useFtueStore(
    (state) => state.activeFlowPriority >= FTUE_FLOW_PRIORITY.primaryJourney,
  )
  const firstJourneyStarted = seen.has(RESUME_BUILDER_JOURNEY_STEPS.addResume)
  const firstJourneyComplete = seen.has(RESUME_BUILDER_JOURNEY_STEPS.continueToAgent)
  const firstJourneyBlocking = firstJourneyStarted && !firstJourneyComplete
  const visibleIdsRef = useRef(new Set<string>())
  visibleIdsRef.current = new Set(items.map((item) => welcomeBackMenuItemStepId(item.id)))

  const itemSignature = items
    .map((item) => `${item.id}:${item.locked ? 'locked' : 'open'}:${item.body}`)
    .join('|')
  const itemsRef = useRef(items)
  itemsRef.current = items
  const allVisibleItemsSeen =
    items.length > 0 && items.every((item) => seen.has(welcomeBackMenuItemStepId(item.id)))
  const viewportReady = !isMobile || mobileMenuOpen || !seen.has(WELCOME_BACK_MENU_STEPS.openMobile)
  const canRunMenu =
    enabled &&
    ready &&
    hydrated &&
    loginCount >= 2 &&
    !firstJourneyBlocking &&
    !primaryJourneyActive &&
    items.length > 0 &&
    viewportReady &&
    !allVisibleItemsSeen
  const canRunMenuRef = useRef(canRunMenu)
  canRunMenuRef.current = canRunMenu

  useEffect(() => {
    if (!enabled) return
    // The signature intentionally re-registers changed RBAC/copy definitions
    // while the ref supplies the corresponding current item objects.
    if (itemSignature.length === 0 && itemsRef.current.length > 0) return
    registerSteps([
      {
        id: WELCOME_BACK_MENU_STEPS.overview,
        flow: WELCOME_BACK_MENU_FLOW,
        phase: WELCOME_BACK_MENU_PHASE,
        order: 1,
        trigger: { type: 'immediate' },
        anchorId: WELCOME_BACK_MENU_ANCHORS.overview,
        presentation: 'coachmark',
        placement: 'right-start',
        skipScope: 'phase',
        condition: () => canRunMenuRef.current && !isMobile,
        content: {
          body: 'Welcome back! Here is everything currently available in your Pelgo workspace.',
        },
      },
      {
        id: WELCOME_BACK_MENU_STEPS.openMobile,
        flow: WELCOME_BACK_MENU_FLOW,
        phase: WELCOME_BACK_MENU_PHASE,
        order: 1,
        trigger: { type: 'immediate' },
        anchorId: WELCOME_BACK_MENU_ANCHORS.openMobile,
        presentation: 'coachmark',
        placement: 'bottom-start',
        dismissOnAction: true,
        skipScope: 'phase',
        condition: () => canRunMenuRef.current && isMobile,
        content: {
          body: 'Welcome back! Open the menu and I’ll show you everything currently available.',
        },
      },
      ...itemsRef.current.map((item, index) => ({
        id: welcomeBackMenuItemStepId(item.id),
        flow: WELCOME_BACK_MENU_FLOW,
        phase: WELCOME_BACK_MENU_PHASE,
        order: index + 10,
        trigger: { type: 'immediate' as const },
        anchorId: welcomeBackMenuItemAnchorId(item.id),
        presentation: 'coachmark' as const,
        placement: isMobile ? ('bottom-start' as const) : ('right' as const),
        skipScope: 'phase' as const,
        scrollIntoView: true,
        // Menu destinations are orientation-only: keep the highlighted link
        // inert and advance with the coachmark's Next/Got-it control so the user
        // stays on the page where this tour began.
        condition: () =>
          canRunMenuRef.current &&
          visibleIdsRef.current.has(welcomeBackMenuItemStepId(item.id)) &&
          (!isMobile || mobileMenuOpen),
        content: {
          body: item.locked
            ? `${item.body} This destination is currently locked until its requirements are met.`
            : item.body,
        },
      })),
    ])
    if (useFtueStore.getState().activeFlow === WELCOME_BACK_MENU_FLOW) {
      useFtueStore.getState().startFlow(WELCOME_BACK_MENU_FLOW, WELCOME_BACK_MENU_PRIORITY)
    }
  }, [enabled, itemSignature, isMobile, mobileMenuOpen])

  useEffect(() => {
    const store = useFtueStore.getState()
    if (!canRunMenu) {
      // A primary journey may become eligible after this navigation flow was
      // already restored from the deferred queue. Re-evaluate against the live
      // guard so no stale sidebar step can appear during a route handoff.
      if (store.activeFlow === WELCOME_BACK_MENU_FLOW) {
        store.startFlow(WELCOME_BACK_MENU_FLOW, WELCOME_BACK_MENU_PRIORITY)
      }
      return
    }
    if (store.activeFlowPriority < FTUE_FLOW_PRIORITY.primaryJourney) {
      store.startFlow(WELCOME_BACK_MENU_FLOW, WELCOME_BACK_MENU_PRIORITY)
    }
  }, [canRunMenu])

  const desktopOverviewAnchorRef = useFtueAnchor(WELCOME_BACK_MENU_ANCHORS.overview)
  const mobileOpenAnchorRef = useFtueAnchor(WELCOME_BACK_MENU_ANCHORS.openMobile)
  const mobileOverviewAnchorRef = useFtueAnchor(WELCOME_BACK_MENU_ANCHORS.overview)
  const desktopItemAnchorRef = useItemAnchorSet()
  const mobileItemAnchorRef = useItemAnchorSet()

  return useMemo(
    () => ({
      desktop: {
        overviewAnchorRef: desktopOverviewAnchorRef,
        itemAnchorRef: desktopItemAnchorRef,
      },
      mobile: {
        openMobileAnchorRef: mobileOpenAnchorRef,
        overviewAnchorRef: mobileOverviewAnchorRef,
        itemAnchorRef: mobileItemAnchorRef,
      },
    }),
    [
      desktopItemAnchorRef,
      desktopOverviewAnchorRef,
      mobileItemAnchorRef,
      mobileOpenAnchorRef,
      mobileOverviewAnchorRef,
    ],
  )
}
